
import React from 'react';
import { useIsSystemAdmin } from '@/hooks/useIsSystemAdmin';
import { useBetaProgramData } from '@/hooks/useBetaProgramData';
import { DatabasePerformanceMonitor } from '@/components/monitoring/DatabasePerformanceMonitor';
import { BetaStatsOverview } from '@/components/beta-program/BetaStatsOverview';
import { RecentUserActivity } from '@/components/beta-program/RecentUserActivity';
import { SystemErrorsMonitor } from '@/components/beta-program/SystemErrorsMonitor';
import { UserMetricsSection } from '@/components/beta-program/UserMetricsSection';
import { OrganizationMetricsWithUsers } from '@/components/beta-program/OrganizationMetricsWithUsers';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, Shield, Info, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export default function BetaProgram() {
  const { data: isSystemAdmin, isLoading: isCheckingRole } = useIsSystemAdmin();
  const { data: betaData, isLoading, refetch } = useBetaProgramData();

  const handleCalculateMetrics = async () => {
    try {
      const { error } = await supabase.rpc('calculate_beta_program_metrics');
      if (error) throw error;
      
      toast.success('Beta program metrics calculated successfully');
      // Refresh the data
      refetch();
    } catch (error) {
      console.error('Error calculating metrics:', error);
      toast.error('Failed to calculate metrics');
    }
  };

  if (isCheckingRole) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-muted-foreground">Checking permissions...</div>
      </div>
    );
  }

  if (!isSystemAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Alert className="max-w-md">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Access denied. This page requires System Administrator privileges.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold">Beta Program Dashboard</h1>
            <p className="text-muted-foreground">
              System-wide monitoring and analytics for the beta program with user identification
            </p>
          </div>
        </div>
        <Button onClick={handleCalculateMetrics} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Calculate Metrics
        </Button>
      </div>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          System Administrator access confirmed. This dashboard provides comprehensive beta program metrics and user analytics.
        </AlertDescription>
      </Alert>

      {isLoading ? (
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="text-muted-foreground">Loading dashboard data...</div>
        </div>
      ) : (
        <div className="space-y-6">
          <BetaStatsOverview 
            userStats={betaData?.userStats} 
            errorSummary={betaData?.errorSummary} 
          />
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <RecentUserActivity recentLogins={betaData?.recentLogins} />
            <SystemErrorsMonitor 
              recentErrors={betaData?.recentErrors}
              errorSummary={betaData?.errorSummary}
            />
          </div>

          {betaData?.userMetrics && (
            <>
              <div>
                <h2 className="text-2xl font-bold mb-4">User Performance Metrics</h2>
                <UserMetricsSection 
                  topUsersByCandidates={betaData.userMetrics.top_users_by_candidates}
                  topUsersByClients={betaData.userMetrics.top_users_by_clients}
                />
              </div>
            </>
          )}

          {betaData?.organizationMetrics && (
            <>
              <div>
                <h2 className="text-2xl font-bold mb-4">Organization Metrics with User Contributors</h2>
                <OrganizationMetricsWithUsers organizationMetrics={betaData.organizationMetrics} />
              </div>
            </>
          )}

          <DatabasePerformanceMonitor />
        </div>
      )}
    </div>
  );
}
